import { Controller } from "@hotwired/stimulus"
import * as amivoiceModule from "../amivoice"
import { AudioRecordingService } from "../services/audio_recording_service"
import { fixWebmDuration } from "@fix-webm-duration/fix"

// Android 9+の複数ストリーム制限をシミュレートするデバッグフラグ
// trueにすると、AmiVoiceだけ無音ストリームを使用（録音は正常なストリームを使用）
const DEBUG_SIMULATE_ANDROID9 = false

// Connects to data-controller="audio-recording"
export default class extends Controller {
  static targets = ["startButton", "completeButton", "history", "transcriptArea", "errorMessage", "recordingStatusText", "timerBar", "timer"]
  static values = {
    examinationId: String,
    engine: String,
    profileWords: String,
    hasRecordings: Boolean,
    hasMemo: Boolean,
    qrEnabled: Boolean
  }
  static outlets = ["recording-header"]

  // Transcript表示/非表示制御（outletsから呼ばれる）
  updateTranscriptVisibility(isVisible) {
    if (this.hasTranscriptAreaTarget) {
      if (isVisible) {
        this.transcriptAreaTarget.classList.remove('hidden')
      } else {
        this.transcriptAreaTarget.classList.add('hidden')
      }
    }
  }

  connect() {
    // AmiVoiceモジュールの初期化
    this.Wrp = amivoiceModule.Wrp
    this.Recorder = amivoiceModule.Recorder
    this.Result = amivoiceModule.Result
    this.wrpConfig = amivoiceModule.wrpConfig
    this.recorderConfig = amivoiceModule.recorderConfig

    // 録音関連の状態
    this.startedAt = null
    this.stream = null
    this.mediaRecorder = null
    this.recordedChunks = []
    this.recordingSessionId = null      // 録音セッションID
    this.recordingCompleted = false     // 録音完了フラグ
    this.isGeneratingSoap = false       // カルテ生成処理中フラグ（生成中はカルテ作成ボタンを操作しない）

    // タイマー関連
    this.timerInterval = null
    this.recordingStartTime = null

    this.setupAmiVoice()

    // 遷移防止のイベントリスナーを設定
    this.setupNavigationPrevention()

    // 初期ボタン状態を設定（録音なし状態で初期化）
    this.updateButtonState(false)

    // URLパラメータをチェックして自動録音開始
    this.checkAutoStart()
  }

  async checkAutoStart() {
    const urlParams = new URLSearchParams(window.location.search)
    const autoStart = urlParams.get('auto_start')

    if (autoStart !== 'true') {
      return
    }

    // URLパラメータをクリア（ブラウザバックとかで録音開始されないようにする）
    const newUrl = window.location.pathname
    window.history.replaceState({}, '', newUrl)

    // LocalStorageから自動録音設定を確認
    const autoRecordEnabled = this.getAutoRecordSetting()

    if (autoRecordEnabled !== 'true') {
      // 自動録音設定がOFFの場合は何もしない
      return
    }

    // ローディング表示
    this.showLoading("録音準備中...")

    // リトライ機能付き録音開始を実行
    const result = await this.startRecordingWithRetry(3)

    if (result.success) {
      // 成功時
      this.hideLoading()
      this.showSuccess("録音を開始しました")
    } else {
      // 失敗時：フォールバック処理
      this.hideLoading()
      this.showError(`録音の自動開始に失敗しました。録音ボタンを押して手動で開始してください。`)
      this.highlightRecordingButton()
      // 10秒後にハイライトを解除
      setTimeout(() => {
        this.removeHighlight()
      }, 10000)
    }
  }

  setupAmiVoice() {
    this.setupWrp()
    this.setupRecorder()
  }

  setupWrp() {
    const wrpConfig = this.wrpConfig

    if (!this.engineValue) {
      throw new Error("音声認識エンジンが設定されていません。管理者にお問い合わせください。")
    }

    this.Wrp.grammarFileNames = this.engineValue  // 文法ファイル名（例: '-a-general'）
    // profileIdは送らない（辞書はprofileWordsでインライン送信するため不要。
    // 指定するとAmiVoiceサーバー側プロファイルを上書きするリスクがある）
    this.Wrp.profileWords = this.profileWordsValue || wrpConfig.profileWords
    this.Wrp.segmenterProperties = wrpConfig.segmenterProperties
    this.Wrp.keepFillerToken = wrpConfig.keepFillerToken
    this.Wrp.resultUpdatedInterval = wrpConfig.resultUpdatedInterval
    this.Wrp.extension = wrpConfig.extension
    this.Wrp.codec = wrpConfig.codec
    this.Wrp.resultType = wrpConfig.resultType
    this.Wrp.checkIntervalTime = wrpConfig.checkIntervalTime

    // イベントハンドラの設定
    this.Wrp.disconnectEnded = () => {
      this.updateButtonState(false)
    }

    this.Wrp.feedDataResumeEnded = () => {
      this.updateButtonState(true)
      this.setupMediaRecorder()

      // 録音開始完了を通知（自動録音のため）
      if (this.recordingStartResolve) {
        this.recordingStartResolve()
        this.recordingStartResolve = null
        this.recordingStartReject = null
      }
    }

    this.Wrp.feedDataPauseEnded = (reason) => {
      this.updateButtonState(false)
      this.stopMediaRecorder()
    }

    this.Wrp.resultFinalized = (result) => {
      const parsed = this.Result.parse(result)
      const text = parsed.text || "(認識結果なし)"
      
      // 現在のセッションに追加
      if (parsed.text && this.currentSessionTexts) {
        this.currentSessionTexts.push(text)
      }
      
      // 履歴に追加
      if (this.hasHistoryTarget && parsed.text) {
        const historyItem = document.createElement('div')
        historyItem.className = 'px-2 text-sm'
        historyItem.style.lineHeight = '1.75'
        historyItem.style.paddingTop = '0.125rem'
        historyItem.style.paddingBottom = '0.125rem'
        historyItem.textContent = text
        this.historyTarget.appendChild(historyItem)
        
        // Auto scroll to bottom
        this.scrollToBottom()
      }
    }

    this.Wrp.TRACE = (message) => console.log("[AmiVoice]", message)
  }

  setupRecorder() {
    const recorderConfig = this.recorderConfig
    this.Recorder.sampleRate = recorderConfig.sampleRate
    this.Recorder.maxRecordingTime = recorderConfig.maxRecordingTime
    this.Recorder.downSampling = recorderConfig.downSampling
    this.Recorder.adpcmPacking = recorderConfig.adpcmPacking
  }

  async startRecording() {
    // 自動開始のハイライトを解除（手動で録音ボタンを押した場合）
    this.removeHighlight()

    if (this.Wrp.isActive()) {
      // 録音停止
      this.startButtonTarget.disabled = true
      this.Wrp.feedDataPause()
    } else {
      // 録音開始
      this.startButtonTarget.disabled = true
      this.startedAt = new Date().toISOString()
      this.currentSessionTexts = []  // 現在のセッションの文字起こしを保存
      this.recordingSessionId = crypto.randomUUID()  // 新しいセッションID
      this.recordingCompleted = false  // 完了フラグリセット
      console.log('[Recording Session Started]', { sessionId: this.recordingSessionId })

      try {
        // 音声ストリームを取得
        if (!this.stream) {
          await this.setupAudioStream()
        }

        // Recorder.jsにストリームを設定（1本のストリームを共有）
        let amivoiceStream = this.stream
        if (DEBUG_SIMULATE_ANDROID9) {
          // デバッグモード：AmiVoiceだけ無音ストリームを使用（Android 9+の複数ストリーム制限をシミュレート）
          amivoiceStream = this.createSilentStream()
          console.log('[DEBUG] Android 9+シミュレーション: AmiVoiceに無音ストリームを設定')
          console.log('[DEBUG] MediaRecorder stream ID:', this.stream.id)
          console.log('[DEBUG] AmiVoice stream ID:', amivoiceStream.id)
          console.log('[DEBUG] ストリームは異なります（2本使用 - Android 9+問題を再現）')
        } else {
          console.log('[通常モード] MediaRecorder stream ID:', this.stream.id)
          console.log('[通常モード] AmiVoice stream ID:', amivoiceStream.id)
          console.log('[通常モード] ストリームは同じです（1本共有 - Android 9+対応）')
        }
        this.Recorder.setStream(amivoiceStream)

        // 録音開始完了を待つPromiseを作成
        const recordingStarted = new Promise((resolve, reject) => {
          this.recordingStartResolve = resolve
          this.recordingStartReject = reject

          // タイムアウト設定（5秒）
          setTimeout(() => {
            if (this.recordingStartReject) {
              this.recordingStartReject(new Error("録音開始がタイムアウトしました"))
              this.recordingStartReject = null
              this.recordingStartResolve = null
            }
          }, 5000)
        })

        // 録音開始を実行
        this.Wrp.feedDataResume()

        // feedDataResumeEndedが呼ばれるまで待つ
        await recordingStarted
      } catch (error) {
        // エラーが発生した場合はボタンを再度有効化
        this.startButtonTarget.disabled = false
        this.log(`録音開始エラー: ${error.message}`)
        throw error
      }
    }
  }

  stopRecording() {
    if (this.Wrp && this.Wrp.isActive()) {
      this.Wrp.feedDataPause()
    }
  }

  async completeRecording() {
    if (this.Wrp.isActive()) {
      // 録音中の場合：録音停止
      this.disableCompleteButton()
      this.showLoading("カルテを作成中...")
      this.isCompleteRecording = true  // 録音完了ボタンから停止したことを記録
      this.Wrp.feedDataPause()
    } else {
      // 録音していない場合：既存の録音・メモからSOAP生成
      this.disableCompleteButton()
      this.showLoading("カルテを作成中...")
      await this.generateSoapWithoutRecording()
    }
  }

  disableCompleteButton() {
    // カルテ生成処理が完了するまでupdateButtonStateにボタンを触らせない
    this.isGeneratingSoap = true
    this.completeButtonTarget.disabled = true
    this.completeButtonTarget.style.opacity = "0.4"
    this.completeButtonTarget.style.cursor = "not-allowed"
  }

  enableCompleteButton() {
    this.isGeneratingSoap = false
    this.completeButtonTarget.disabled = false
    this.completeButtonTarget.style.opacity = "1"
    this.completeButtonTarget.style.cursor = "pointer"
  }

  async generateSoapWithoutRecording() {
    try {
      const templateId = localStorage.getItem('device_soap_prompt_template_id') ||
        document.querySelector('[data-soap-prompt-selector-target="select"]')?.value
      const body = templateId ? JSON.stringify({ soap_prompt_template_id: templateId }) : JSON.stringify({})

      const response = await fetch(`/examinations/${this.examinationIdValue}/generate_soap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: body
      })

      const result = await response.json()

      if (result.success) {
        // 成功時：ページをリロードしてカルテを表示
        // QR有効テナントはQRモーダル自動表示フラグを付ける
        if (this.qrEnabledValue) {
          window.location.href = window.location.pathname + '?show_qr=1'
        } else {
          window.location.reload()
        }
      } else {
        this.hideLoading()
        alert('カルテ生成に失敗しました: ' + result.error)
        this.enableCompleteButton()
      }
    } catch (error) {
      this.hideLoading()
      console.error('SOAP生成エラー:', error)
      alert('カルテ生成中にエラーが発生しました')
      this.enableCompleteButton()
    }
  }

  updateButtonState(isRecording) {
    // ヘッダーの背景色を変更（outletsを使用）
    this.recordingHeaderOutlet.setRecordingState(isRecording)

    // 左カラムの録音中表示を切り替え
    this.updateRecordingStatus(isRecording)

    // タイマーバーの表示/非表示とタイマーの制御
    this.updateTimerBar(isRecording)

    if (this.hasStartButtonTarget) {
      this.startButtonTarget.disabled = false
      const startSpan = this.startButtonTarget.querySelector('span')
      const iconDiv = this.startButtonTarget.querySelector('div')

      if (startSpan) {
        startSpan.textContent = isRecording ? "一時停止" : "録音開始"
      }

      // アイコンを変更
      if (isRecording) {
        // 一時停止アイコン（白い丸の中に赤い縦線2本）
        iconDiv.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="white"/>
            <rect x="7" y="6" width="2" height="8" fill="#FF0032"/>
            <rect x="11" y="6" width="2" height="8" fill="#FF0032"/>
          </svg>
        `
        iconDiv.className = ''
        iconDiv.style.backgroundColor = ''
      } else {
        // 録音開始アイコン（赤い丸）
        iconDiv.innerHTML = ''
        iconDiv.className = 'w-4 h-4 rounded-full'
        iconDiv.style.backgroundColor = '#FF0032'
      }

      // 録音状態に応じて色を変更
      if (isRecording) {
        this.startButtonTarget.style.backgroundColor = "#FF0032"
        this.startButtonTarget.style.color = "#FFFFFF"
      } else {
        this.startButtonTarget.style.backgroundColor = "#F4F2F2"
        this.startButtonTarget.style.color = "#666666"
      }
    }

    // カルテ生成処理中はボタン状態を上書きしない（グレーアウトを維持）
    if (this.hasCompleteButtonTarget && !this.isGeneratingSoap) {
      // カルテ作成ボタンは以下のいずれかで有効化：
      // - 録音中
      // - 既存の音声録音がある
      // - 診察メモがある
      const canComplete = isRecording || this.hasRecordingsValue || this.hasMemoValue
      this.completeButtonTarget.disabled = !canComplete

      // 状態に応じて透明度とカーソルを変更
      if (canComplete) {
        this.completeButtonTarget.style.opacity = "1"
        this.completeButtonTarget.style.cursor = "pointer"
      } else {
        this.completeButtonTarget.style.opacity = "0.4"
        this.completeButtonTarget.style.cursor = "not-allowed"
      }
    }
  }

  updateRecordingStatus(isRecording) {
    if (isRecording) {
      this.recordingStatusTextTarget.classList.remove('hidden')
    } else {
      this.recordingStatusTextTarget.classList.add('hidden')
    }
  }

  updateTimerBar(isRecording) {
    if (isRecording) {
      this.timerBarTarget.classList.remove('hidden')
      this.startTimer()
    } else {
      this.timerBarTarget.classList.add('hidden')
      this.stopTimer()
    }
  }

  scrollToBottom() {
    if (this.hasHistoryTarget) {
      this.historyTarget.scrollTop = this.historyTarget.scrollHeight
    }
  }

  startTimer() {
    this.recordingStartTime = Date.now()
    this.updateTimerDisplay()

    this.timerInterval = setInterval(() => {
      this.updateTimerDisplay()
    }, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
    this.recordingStartTime = null
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = "00:00"
    }
  }

  updateTimerDisplay() {
    if (!this.recordingStartTime || !this.hasTimerTarget) return

    const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000)
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0')
    const seconds = (elapsed % 60).toString().padStart(2, '0')
    this.timerTarget.textContent = `${minutes}:${seconds}`
  }

  log(message) {
    console.log("[AudioRecording]", message)
  }

  showError(message) {
    // エラーメッセージを表示
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
    }
    // 一定時間後に非表示
    setTimeout(() => {
      this.hideError()
    }, 5000)
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
    }
  }

  // ローディング表示
  showLoading(message = "録音を準備中...") {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
      this.errorMessageTarget.classList.remove('text-red-600', 'border-red-200')
      this.errorMessageTarget.classList.add('text-blue-600', 'border-blue-200')
    }
  }

  // ローディング状態を非表示
  hideLoading() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
      // 色を元に戻す
      this.errorMessageTarget.classList.remove('text-blue-600', 'border-blue-200')
      this.errorMessageTarget.classList.add('text-red-600', 'border-red-200')
    }
  }

  // 成功メッセージを表示
  showSuccess(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden', 'text-red-600', 'border-red-200', 'text-blue-600', 'border-blue-200')
      this.errorMessageTarget.classList.add('text-green-600', 'border-green-200')
    }

    // 3秒後に非表示
    setTimeout(() => {
      this.hideSuccess()
    }, 3000)
  }

  hideSuccess() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
      this.errorMessageTarget.classList.remove('text-green-600', 'border-green-200')
      this.errorMessageTarget.classList.add('text-red-600', 'border-red-200')
    }
  }

  // 手動録音ボタンを強調表示（自動録音開始失敗時のフォールバック用）
  highlightRecordingButton() {
    if (this.hasStartButtonTarget) {
      // ボタンを点滅させるアニメーションを追加
      this.startButtonTarget.style.animation = 'pulse 1.5s ease-in-out infinite'

      if (!document.getElementById('recording-pulse-animation')) {
        const style = document.createElement('style')
        style.id = 'recording-pulse-animation'
        style.textContent = `
          @keyframes pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 186, 143, 0.7); }
            50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(25, 186, 143, 0); }
          }
        `
        document.head.appendChild(style)
      }
    }
  }

  removeHighlight() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.style.animation = ''
    }
  }

  // マイクが選択されているか確認し、未選択の場合は自動選択
  async ensureMicrophoneSelected() {
    const selectedDeviceId = localStorage.getItem('selectedMicrophoneId')

    if (selectedDeviceId) {
      return true
    }

    try {
      // デバイス一覧を取得
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(device => device.kind === 'audioinput')

      if (audioInputs.length === 0) {
        return false
      }

      // 最初のマイクを自動選択
      const firstMic = audioInputs[0]
      localStorage.setItem('selectedMicrophoneId', firstMic.deviceId)

      // マイクセレクターのUIも更新（存在する場合）
      const micSelect = document.getElementById('microphone-select')
      if (micSelect) {
        micSelect.value = firstMic.deviceId
      }
      return true
    } catch (error) {
      return false
    }
  }

  // 初期化完了を待つ（最大5秒）
  async waitForInitialization() {
    const maxWaitTime = 5000 // 5秒
    const checkInterval = 100 // 100msごとにチェック
    let elapsed = 0

    while (elapsed < maxWaitTime) {
      // AmiVoiceが初期化されているかチェック
      if (this.Wrp && typeof this.Wrp.isActive === 'function') {
        return true
      }

      await new Promise(resolve => setTimeout(resolve, checkInterval))
      elapsed += checkInterval
    }
    return false
  }

  // リトライ機能付き録音開始（指数バックオフ）
  async startRecordingWithRetry(maxRetries = 3) {
    const delays = [1000, 2000, 3000] // 各リトライの遅延（ms）

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // 初期化完了を待つ
        const initialized = await this.waitForInitialization()
        if (!initialized) {
          throw new Error("初期化タイムアウト")
        }

        // マイクが選択されているか確認
        const micSelected = await this.ensureMicrophoneSelected()
        if (!micSelected) {
          throw new Error("マイクの選択に失敗しました")
        }

        // 録音開始を試みる（Promiseで録音開始完了を待つ）
        await this.startRecording()

        // 成功したらループを抜ける
        return { success: true }
      } catch (error) {
        // 最後の試行の場合は失敗を返す
        if (attempt === maxRetries - 1) {
          return {
            success: false,
            error: error.message,
            attempts: maxRetries
          }
        }

        // 次の試行まで待機（指数バックオフ）
        const delay = delays[attempt] || 2000
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    return { success: false, error: "最大試行回数に達しました" }
  }

  async setupAudioStream() {
    try {
      // LocalStorageから選択されたマイクIDを取得
      const selectedDeviceId = localStorage.getItem('selectedMicrophoneId')
      
      if (!selectedDeviceId) {
        // マイクが選択されていない場合はエラー
        this.showError('マイクが選択されていません。ヘッダーからマイクを選択してください。')
        throw new Error('Microphone not selected')
      }
      
      // 選択されたマイクで音声ストリームを取得
      const constraints = {
        audio: {
          deviceId: selectedDeviceId === 'default' ? undefined : { exact: selectedDeviceId },
          echoCancellation: false  // AmiVoice用の設定（元のrecorder.jsの設定を維持）
        }
      }
      
      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    } catch (error) {
      // マイクが選択されていない場合は再throwしてstartRecordingで処理
      if (error.message === 'Microphone not selected') {
        throw error
      }
      
      // getUserMediaエラーの場合：選択されたdeviceIdが無効になっている可能性
      if (error.name === 'NotFoundError' || error.name === 'DeviceNotFoundError' || 
          error.name === 'NotReadableError' || error.name === 'OverconstrainedError') {
        // LocalStorageをクリアして再選択を促す
        localStorage.removeItem('selectedMicrophoneId')
        this.showError('選択されたマイクにアクセスできません。ヘッダーからマイクを選択し直してください。')
      } else {
        this.showError('音声の取得に失敗しました。マイクの権限を確認してください。')
      }
      this.log(`音声ストリーム取得エラー: ${error.message}`)
      throw error
    }
  }

  setupMediaRecorder() {
    if (!this.stream) {
      return
    }

    // テスト: MP4のみに限定
    const mimeType = 'audio/mp4'

    console.log('[MediaRecorder mimeType TEST]', {
      mimeType,
      isSupported: MediaRecorder.isTypeSupported(mimeType)
    })

    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType })
    this.recordedChunks = []
    this.recordingMimeType = mimeType  // 保存時に使用するため保存

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        console.log('[ondataavailable]', {
          sessionId: this.recordingSessionId,
          size: event.data.size,
          type: event.data.type,  // 実際の録音形式を確認
          chunksBefore: this.recordedChunks.length,
          timestamp: new Date().toISOString()
        })
        this.recordedChunks.push(event.data)
        console.log('[chunk pushed]', {
          chunksAfter: this.recordedChunks.length
        })
      }
    }

    this.mediaRecorder.onstop = async () => {
      console.log('[onstop]', {
        sessionId: this.recordingSessionId,
        chunkCount: this.recordedChunks.length,
        totalSize: this.recordedChunks.reduce((sum, c) => sum + c.size, 0),
        timestamp: new Date().toISOString()
      })
      await this.handleRecordingComplete()
    }

    // Safari requires timeslice parameter for ondataavailable to fire
    this.mediaRecorder.start(1000) // Request data every 1 second
  }

  stopMediaRecorder() {
    console.log('[stopMediaRecorder called]', {
      sessionId: this.recordingSessionId,
      state: this.mediaRecorder?.state,
      timestamp: new Date().toISOString()
    })

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
      console.log('[mediaRecorder.stop() called]')
    } else {
      console.log('[mediaRecorder.stop() skipped]', {
        hasRecorder: !!this.mediaRecorder,
        state: this.mediaRecorder?.state
      })
    }
  }

  // デバッグ用：Android 9+の複数ストリーム制限をシミュレートする無音ストリームを生成
  createSilentStream() {
    const audioContext = new AudioContext()
    const destination = audioContext.createMediaStreamDestination()
    const oscillator = audioContext.createOscillator()
    oscillator.frequency.value = 0 // 無音
    oscillator.connect(destination)
    oscillator.start()
    return destination.stream
  }

  async handleRecordingComplete() {
    const sessionId = this.recordingSessionId
    const chunkCount = this.recordedChunks.length
    const totalSize = this.recordedChunks.reduce((sum, c) => sum + c.size, 0)
    const callNumber = this.recordingCompleted ? 2 : 1

    console.log('[handleRecordingComplete START]', {
      callNumber,
      sessionId,
      chunkCount,
      totalSize,
      completed: this.recordingCompleted,
      timestamp: new Date().toISOString(),
      stack: new Error().stack  // 呼び出し元を追跡（完全版）
    })

    // セッション単位での重複防止
    if (this.recordingCompleted) {
      console.warn('[DUPLICATE CALL PREVENTED - BUT LOGGED FULLY]', {
        callNumber,
        sessionId,
        chunkCount,
        totalSize,
        timestamp: new Date().toISOString(),
        stack: new Error().stack  // 2回目の呼び出し元も記録
      })
      return
    }

    // 完了フラグを立てる（このセッションでは二度と保存しない）
    this.recordingCompleted = true
    console.log('[Recording marked as completed]', { sessionId })

    try {
      const endedAt = new Date()
      const durationMs = endedAt - new Date(this.startedAt)

      // 今回の録音セッションで認識されたテキストを取得（改行で結合）
      const transcript = this.currentSessionTexts ? this.currentSessionTexts.join('\n') : ''

      // 音声ファイルを作成（録音時に使用した形式を使用）
      const mimeType = this.recordingMimeType || "audio/webm"
      const blob = new Blob(this.recordedChunks, { type: mimeType })
      console.log('[Blob created]', {
        sessionId,
        size: blob.size,
        type: blob.type,
        mimeType
      })

      const fixedBlob = await fixWebmDuration(blob, durationMs)
      console.log('[Fixed blob created]', {
        sessionId,
        size: fixedBlob.size
      })

      // 診察IDを取得
      const examinationId = this.examinationIdValue

      // APIに保存
      console.log('[Saving to API]', { sessionId, examinationId })
      const result = await AudioRecordingService.save(examinationId, {
        startedAt: new Date(this.startedAt),
        endedAt: endedAt,
        transcript: transcript,
        audioFile: fixedBlob,
        createSoap: this.isCompleteRecording || false  // 録音完了ボタンの場合true
      })

      console.log('[API Save Result]', {
        sessionId,
        success: result.success
      })

      if (result.success) {
        // ページをリロードして一覧を更新
        // 録音完了ボタン経由かつQR有効テナントはQRモーダル自動表示フラグを付ける
        if (this.isCompleteRecording && this.qrEnabledValue) {
          this.isCompleteRecording = false
          window.location.href = window.location.pathname + '?show_qr=1'
        } else {
          this.isCompleteRecording = false
          window.location.reload()
        }
      } else {
        this.log(`録音データ保存エラー: ${result.error}`)
        this.isCompleteRecording = false
        this.enableCompleteButton()
      }
    } catch (error) {
      console.error('[handleRecordingComplete ERROR]', {
        sessionId,
        error: error.message,
        stack: error.stack
      })
      this.log(`録音完了処理エラー: ${error.message}`)
      this.isCompleteRecording = false
      this.enableCompleteButton()
    }
  }




  // 録音中かどうかを返す
  isRecording() {
    return this.Wrp && this.Wrp.isActive()
  }

  // LocalStorageから自動録音設定を取得
  getAutoRecordSetting() {
    // デフォルトは "false" (OFF)
    const saved = localStorage.getItem('autoRecordEnabled')
    return saved !== null ? saved : 'false'
  }

  // 遷移防止の設定
  setupNavigationPrevention() {
    // Turbo遷移を防止
    this.turboBeforeVisitHandler = (event) => {
      if (this.isRecording()) {
        event.preventDefault()
        alert('現在録音中のため、ページを移動できません。録音を停止してから再度お試しください。')
      }
    }
    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)

    // ブラウザの閉じる・リロード・戻るを防止
    this.beforeUnloadHandler = (event) => {
      if (this.isRecording()) {
        event.preventDefault()
        event.returnValue = '' // Chrome/Edge では空文字列が必要
        return '' // その他のブラウザ用
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  disconnect() {
    // イベントリスナーを削除
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
    // クリーンアップ
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }
  }

}
